import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, BookOpen, FileText, Users, Globe, ChevronRight, Star } from 'lucide-react';
import { RatingSummary } from '../components/StarRating';

export default function LandingPage() {
  const [articles, setArticles] = useState([]);
  const { user } = useAuth();
  const curated = articles.some(a => a.featured);

  useEffect(() => {
    articlesAPI.featured().then(res => setArticles(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-0.5 w-8 bg-trans-blue rounded-full" />
                <span className="text-xs font-sans font-semibold tracking-widest uppercase text-trans-blue">Revista Cientifica</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-dark leading-[1.1] tracking-tight">
                Archivos Latinoamericanos de Estudios Trans
              </h1>
              <p className="mt-6 text-base lg:text-lg font-sans text-gray-500 leading-relaxed max-w-lg">
                Publicacion cientifica de alto impacto dedicada a la investigacion y el conocimiento
                en salud trans y genero diverso en America Latina.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button asChild size="lg" className="bg-slate-dark hover:bg-slate-dark/90 font-sans" data-testid="hero-archive-btn">
                  <Link to="/archivo">
                    Explorar Archivo <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-sans" data-testid="hero-submit-btn">
                  <Link to={user ? "/panel/enviar" : "/registro"}>
                    Enviar Manuscrito
                  </Link>
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-10">
                <div className="flex items-center gap-2 text-gray-400">
                  <FileText className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-sans">Revision por pares</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-sans">Acceso abierto</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Users className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-sans">Comunidad global</span>
                </div>
              </div>
            </div>

            <div className="animate-fade-up-delay-2 hidden lg:block">
              {articles[0] && (
                <Link to={`/articulo/${articles[0].id}`} className="relative block group" data-testid="hero-featured-card">
                  <div className="absolute -inset-4 bg-trans-blue/5 rounded-lg transition-colors group-hover:bg-trans-blue/10" />
                  <div className="relative bg-white rounded-sm border border-gray-100 p-8 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-sans font-semibold tracking-widest uppercase text-trans-pink">Volumen {articles[0].volumen}, Numero {articles[0].numero}</span>
                      {articles[0].featured && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-sans font-semibold uppercase tracking-wider text-amber-600">
                          <Star className="w-3 h-3" fill="currentColor" /> Destacado
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif font-semibold text-slate-dark text-lg leading-snug mt-3 group-hover:text-trans-blue transition-colors line-clamp-3">
                      {articles[0].titulo}
                    </h3>
                    <p className="text-sm font-sans text-gray-400 mt-2">{articles[0].autores}</p>
                    <p className="text-sm font-serif text-gray-500 mt-3 leading-relaxed line-clamp-3">{articles[0].abstract_es}</p>
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {(articles[0].palabras_clave || []).slice(0, 2).map((kw, i) => (
                        <Badge key={kw} variant="outline" className={`text-[10px] font-sans ${i % 2 === 0 ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-pink-50 text-pink-700 border-pink-100'}`}>{kw}</Badge>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <div className="h-0.5 w-6 bg-trans-blue rounded-full" />
                        <div className="h-0.5 w-6 bg-trans-pink rounded-full" />
                        <div className="h-0.5 w-6 bg-gray-200 rounded-full" />
                        <div className="h-0.5 w-6 bg-trans-pink rounded-full" />
                        <div className="h-0.5 w-6 bg-trans-blue rounded-full" />
                      </div>
                      <RatingSummary avg={articles[0].rating_avg} count={articles[0].rating_count} testId="hero-rating" />
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Articles (admin-curated; falls back to latest) */}
      {articles.length > 0 && (
        <section className="bg-white py-24 border-t border-gray-50" data-testid="featured-articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-0.5 w-8 bg-trans-pink rounded-full" />
              <span className="text-xs font-sans font-semibold tracking-widest uppercase text-trans-pink">{curated ? 'Seleccion editorial' : 'Publicaciones'}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-dark mt-2" data-testid="featured-heading">{curated ? 'Papers Destacados' : 'Articulos Recientes'}</h2>
            <p className="text-base font-sans text-gray-400 mt-3 max-w-lg">
              {curated ? 'Trabajos seleccionados por el equipo editorial de Archivos Trans.' : 'Investigaciones recientes en estudios trans y salud de genero diverso.'}
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {articles.map((article, i) => (
                <Link
                  key={article.id}
                  to={`/articulo/${article.id}`}
                  className={`group relative block p-6 bg-white border rounded-sm hover:shadow-sm transition-all duration-200 hover:-translate-y-0.5 animate-fade-up-delay-${Math.min(i + 1, 3)} ${article.featured ? 'border-amber-200 hover:border-amber-300' : 'border-gray-100 hover:border-gray-200'}`}
                  data-testid={`featured-article-${i}`}
                >
                  {article.featured && (
                    <span className="absolute top-0 right-6 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-sans font-semibold uppercase tracking-wider bg-amber-400 text-slate-dark px-2.5 py-1 rounded-full shadow-sm" data-testid={`featured-badge-${i}`}>
                      <Star className="w-3 h-3" fill="currentColor" /> Destacado
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-[10px] font-sans bg-blue-50 text-blue-700 border-blue-100">
                      Vol. {article.volumen} N.{article.numero}
                    </Badge>
                    <span className="text-[10px] font-sans text-gray-400">{article.categoria}</span>
                  </div>
                  <h3 className="font-serif font-semibold text-slate-dark leading-snug group-hover:text-trans-blue transition-colors line-clamp-3">
                    {article.titulo}
                  </h3>
                  <p className="text-sm font-sans text-gray-400 mt-2">{article.autores}</p>
                  <p className="text-sm font-serif text-gray-500 mt-3 leading-relaxed line-clamp-2">{article.abstract_es}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="flex items-center gap-1 text-trans-blue text-sm font-sans font-medium">
                      Leer articulo <ChevronRight className="w-3 h-3" />
                    </span>
                    <RatingSummary avg={article.rating_avg} count={article.rating_count} testId={`featured-rating-${i}`} />
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button asChild variant="outline" className="font-sans" data-testid="view-all-articles-btn">
                <Link to="/archivo">Ver todo el archivo <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-slate-dark" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">Contribuye al conocimiento</h2>
          <p className="text-base font-sans text-gray-400 mt-4 max-w-xl mx-auto leading-relaxed">
            Envia tu investigacion y forma parte de la comunidad cientifica mas importante
            en estudios trans de America Latina.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Button asChild size="lg" className="bg-trans-blue hover:bg-trans-blue/90 text-slate-dark font-sans font-semibold" data-testid="cta-register-btn">
              <Link to={user ? "/panel/enviar" : "/registro"}>{user ? "Enviar Manuscrito" : "Crear Cuenta Gratuita"}</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10 font-sans" data-testid="cta-guidelines-btn">
              <Link to="/pagina/lineamientos">Ver Lineamientos</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
